import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Record } from "@common/data-models/records/record";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { ObjectProperties } from "@common/data-models/service/object-properties";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { Util } from "@common/utils/util";
import { ErrorTypes } from '@common/data-models/misc/errors';
import { DataStore } from "@common/data-models/data-store/data-store";
import { AttachmentList, AttachmentListItem } from "@common/data-models/complex-types/attachment-list";

import { saveAs } from 'file-saver';

import { LanguageSettings } from "@common/services/language-settings";
import { BasicContentService } from '@common/services/basic-content-service';
import { ObjectTypes } from '@common/enums/object-types';
import { VisualStateService } from '@common/services/visual-state-service';
import { GlobalTablesService } from "@common/services/global-tables-service";
import { Dialog } from '@angular/cdk/dialog';
import { AttachmentRenameComponent } from '../components/attachments-list/modal/rename/attachment-rename';

@Injectable({ providedIn: 'root' })
export class AttachmentService {
    public maxExternalDocumentSize: number = 512000; // default in bytes (same as the WPF Client logic )
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public basicContentService: BasicContentService,
        private visualStateService: VisualStateService,
        public globalTablesService: GlobalTablesService,
        private dialogService: Dialog
    ) {
        this.globalTablesService.globalTablesLoaded.subscribe(() => {
            if (this.visualStateService.ConfigSettings["MaxExternalDocSize"]) {
                this.maxExternalDocumentSize = parseInt(this.visualStateService.ConfigSettings["MaxExternalDocSize"]);
                if (this.maxExternalDocumentSize > 3000000) { // if it's more than 3000000 bytes, limit it to 3000000
                    this.maxExternalDocumentSize = 3000000;
                }
            }
            if (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].setMaxFileSize) {
                window["ANDROID_WRAPPER"].setMaxFileSize(this.maxExternalDocumentSize);
            }            
        });
    }

    
    public async deleteAttachment(record: Record, attachment: AttachmentListItem, referalID: string): Promise<void> {
        try {
            let data = {
                "ExternalFiles": record["attachments"],
                "Type": record.type
            };
            let res = await this.basicContentService.updateBasicContent<Record>(record.database, record.path.fullPath, record.guid, false, data, false, null);
            if (res && res.status) {
                //TODO?
                let res = await this.basicContentService.deleteBasicContent(attachment.database, record.path.fullPath, attachment.guid, ObjectTypes.EXTERNAL, referalID);
            }
        } catch (ex) {
        }
    }

    public async createAttachment<T extends Record>(database: string, path: string, parentGuid: string, keepApproved: boolean, fields: { [id: string]: any }, referal: string): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            let fieldsToUpdate = this.nomWebServicesJson.Convertor.toENPSFields(fields);
            Util.escapeSpecialCharsForFileName(fields)
            // always need to include create time, let WS handle expires
            fieldsToUpdate.push({
                FieldName: "CreateTime",
                FieldValue: new Date().toJSON()
            });
            requestParams = {
                database: database,
                path: path,
                parentGuid: parentGuid,
                keepApproved: keepApproved,
                fieldsToUpdate: fieldsToUpdate
            };
            response = await this.nomWebServicesJson.put(
                "/api/BasicContent", 
                JSON.stringify(requestParams), {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create content",
                detailMessage: `Unable to create content. \n\rStackTrace: NomWebServicesJson.createAttachment(); \n\rError: ${error}`
            }, ErrorTypes.WARNING);
            return new ServiceResponse<T>(false, "Unable to create basic content " + error.toString());
        }
        let objectProperties = new ObjectProperties(response.body);
        let newAttachments = new AttachmentList();
        let externalFiles = objectProperties.getAs<Array<any>>("ExternalFiles");
        for (let entry of externalFiles) {
            let entryObjectProperties = new ObjectProperties(entry);
            newAttachments.add(new AttachmentListItem(
                entryObjectProperties.getAs<string>("DATABASE"),
                entryObjectProperties.getAs<string>("PATH"),
                entryObjectProperties.getAs<string>("GUID"),
                entryObjectProperties.getAs<string>("TITLE")
            ));
        }
        let record = DataStore.byGuid<T>(parentGuid, referal);
        record.attachments = newAttachments;
        return new ServiceResponse<T>(true, DataStore.byGuid<T>(record.guid, referal));
    }

    public async downloadAttachment(database: string, path: string, guid: string, title: string): Promise<ServiceResponse<void>> {
        let response: Blob = null;

        if (this.visualStateService.hasIOSInterOp()) {
            window["webkit"].messageHandlers.interOp.postMessage({ "name": "startShare" });
        }

        try {
            response = await this.nomWebServicesJson.getBlob(
                "/api/BasicContent", 
                {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'blob',
                params: {
                    database: database,
                    path: path,
                    guid: guid,
                    hithighlightTerm: ""
                }
            });
        } catch (error) {
            if (this.visualStateService.hasIOSInterOp()) {
                window["webkit"].messageHandlers.interOp.postMessage({ "name": "stopShare" });
            }
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to download content",
                detailMessage: `Unable to download content. \n\rStackTrace: NomWebServicesJson.downloadAttachment(); \n\rError: ${error}`
            }, ErrorTypes.WARNING);
            return new ServiceResponse<void>(false, "Unable to download basic content " + error.toString());
        }


        if (navigator.userAgent.match(/iPad|iPod|iPhone/ig) || (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].saveFile)) {
            this.shareFile(response, response.type, title);
        } else { //placeholder for Android
            saveAs(response, title);
        }

        return new ServiceResponse<void>(true);
    }

    protected shareFile(fileblob, type, title) {
        if (
            (navigator.userAgent.match(/iPad|iPod|iPhone/ig) && window["webkit"] && window["webkit"].messageHandlers)
            || (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].saveFile)
        ) {

            var reader = new FileReader();
            reader.readAsDataURL(fileblob);
            reader.onloadend = function () {
                let data = {
                    "filename": title,
                    "type": type,
                    "blob": reader.result.toString().split(",")[1]
                };
                if (window["webkit"] && window["webkit"].messageHandlers && window["webkit"].messageHandlers.interOp && window["webkit"].messageHandlers.interOp.postMessage) {
                    window["webkit"].messageHandlers.interOp.postMessage({ "name": "shareFile", "fileData": JSON.stringify(data) });
                } else if (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].saveFile) {
                    window["ANDROID_WRAPPER"].saveFile(JSON.stringify(data));
                }
            }
        }
    }
}
